import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import AdminEvent from "@/components/torq/AdminEvent";
import AdminPromos from "@/components/torq/AdminPromos";
import AdminCheckin from "@/components/torq/AdminCheckin";
import AdminBookings from "@/components/torq/AdminBookings";
import AdminChanges from "@/components/torq/AdminChanges";
import AdminReports from "@/components/torq/AdminReports";
import AdminAudit from "@/components/torq/AdminAudit";
import AdminInbox from "@/components/torq/AdminInbox";

const API = process.env.REACT_APP_BACKEND_URL;
const ADMIN_TABS = ["BOOKINGS", "CHANGES", "INBOX", "EVENT", "PROMOS", "CHECK-IN", "REPORTS", "AUDIT"];

export default function AdminPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem("tq_admin") || "");
  const [role, setRole] = useState(() => sessionStorage.getItem("tq_role") || "");
  const [tab, setTab] = useState("BOOKINGS");
  const [input, setInput] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const unlock = async (value) => {
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/admin/stats`, { headers: { "X-Admin-Token": value } });
      if (res.ok) {
        sessionStorage.setItem("tq_admin", value);
        sessionStorage.setItem("tq_role", "admin");
        setToken(value);
        setRole("admin");
        setTab("BOOKINGS");
        return;
      }
      const probe = await fetch(`${API}/api/admin/checkin`, {
        method: "POST",
        headers: { "X-Admin-Token": value, "Content-Type": "application/json" },
        body: JSON.stringify({ token: "role-probe-token" }),
      });
      if (probe.status !== 401) {
        sessionStorage.setItem("tq_admin", value);
        sessionStorage.setItem("tq_role", "gate");
        setToken(value);
        setRole("gate");
        setTab("CHECK-IN");
        return;
      }
      setError("Invalid access token");
    } catch {
      setError("Could not reach the server");
    } finally {
      setBusy(false);
    }
  };

  const lock = () => {
    sessionStorage.removeItem("tq_admin");
    sessionStorage.removeItem("tq_role");
    setToken("");
    setRole("");
  };

  if (!token || !role) {
    return (
      <main className="min-h-screen bg-[#161614] text-[#f0eee4] font-body antialiased flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <p className="font-mono text-[10px] tracking-[0.35em] text-[#6e6e6a]">TORQ CIRCUIT</p>
          <h1 className="mt-3 font-display font-extrabold uppercase text-4xl">ORGANIZER ACCESS</h1>
          <input
            type="password"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && input && unlock(input)}
            placeholder="ACCESS TOKEN"
            data-testid="admin-token-input"
            className="mt-8 w-full bg-transparent border border-[#454540] px-4 py-4 font-mono text-sm tracking-[0.1em] placeholder:text-[#6e6e6a] focus:outline-none focus:border-[#f0eee4]"
          />
          {error && (
            <p className="mt-3 font-mono text-[11px] tracking-[0.1em] text-red-400" data-testid="admin-error">{error}</p>
          )}
          <button
            onClick={() => unlock(input)}
            disabled={busy || !input}
            data-testid="admin-unlock-btn"
            className="mt-4 w-full btn-solid bg-[#ff5a1f] text-[#0c0c0a] font-mono text-xs tracking-[0.25em] py-4 min-h-[52px] hover:bg-white disabled:opacity-40"
          >
            {busy ? "CHECKING…" : "UNLOCK DASHBOARD"}
          </button>
          <Link to="/" className="mt-6 inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] text-[#6e6e6a] hover:text-[#f0eee4] transition-colors">
            <ArrowLeft size={12} /> BACK TO SITE
          </Link>
        </div>
      </main>
    );
  }

  const tabs = role === "gate" ? ["CHECK-IN"] : ADMIN_TABS;

  return (
    <main className="min-h-screen bg-[#161614] text-[#f0eee4] font-body antialiased" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between">
          <Link to="/" data-testid="admin-back" className="font-mono text-[11px] tracking-[0.25em] text-[#a3a39f] hover:text-[#f0eee4] transition-colors inline-flex items-center gap-2">
            <ArrowLeft size={14} /> SITE
          </Link>
          <div className="flex items-center gap-4">
            {role === "gate" && (
              <span className="font-mono text-[9px] tracking-[0.2em] text-[#a3a39f] border border-[#454540] px-2 py-1" data-testid="admin-role-badge">
                GATE MODE
              </span>
            )}
            <button onClick={lock} data-testid="admin-lock" className="font-mono text-[10px] tracking-[0.2em] text-[#6e6e6a] hover:text-[#f0eee4] transition-colors">
              LOCK
            </button>
          </div>
        </div>
        <h1 className="mt-8 font-display font-extrabold uppercase text-4xl lg:text-5xl" data-testid="admin-title">CONTROL ROOM</h1>

        <div className="mt-10 flex flex-wrap gap-px bg-[#2e2e2a] border border-[#2e2e2a] w-fit max-w-full" data-testid="admin-tabs">
          {tabs.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`admin-tab-${t.toLowerCase().replace(/[^a-z]/g, "")}`}
              className={`px-5 py-3 min-h-[48px] font-mono text-[10px] tracking-[0.2em] transition-colors ${tab === t ? "bg-[#ff5a1f] text-[#0c0c0a]" : "bg-[#161614] text-[#a3a39f] hover:text-[#f0eee4]"}`}
            >
              {t}
            </button>
          ))}
        </div>

        <div className="mt-10">
          {tab === "BOOKINGS" && role === "admin" && <AdminBookings token={token} />}
          {tab === "CHANGES" && role === "admin" && <AdminChanges token={token} />}
          {tab === "INBOX" && role === "admin" && <AdminInbox token={token} />}
          {tab === "EVENT" && role === "admin" && <AdminEvent token={token} />}
          {tab === "PROMOS" && role === "admin" && <AdminPromos token={token} />}
          {tab === "CHECK-IN" && <AdminCheckin token={token} />}
          {tab === "REPORTS" && role === "admin" && <AdminReports token={token} />}
          {tab === "AUDIT" && role === "admin" && <AdminAudit token={token} />}
        </div>
      </div>
    </main>
  );
}
